/** Shared response to the closed, finite canyon solids. Coordinates are local metres. */
import { cliffHit, type CanyonCliffField, type CliffHit } from './canyonCliffs.js';

export interface CliffMotionScratch {
  p: number[]; a: number[]; b: number[]; delta: number[]; hit: CliffHit;
}
export function cliffMotionScratch(): CliffMotionScratch {
  return { p: [0, 0, 0], a: [0, 0, 0], b: [0, 0, 0], delta: [0, 0, 0], hit: cliffHit() };
}
const SKIN_M = .003;
function endpoints(s: CliffMotionScratch, up: readonly number[], height: number, radius: number): void {
  for (let i = 0; i < 3; i++) {
    s.a[i] = s.p[i] + up[i] * radius;
    s.b[i] = s.p[i] + up[i] * Math.max(radius, height - radius);
  }
}
function rejectInto(v: number[], n: readonly number[]): void {
  const inward = v[0] * n[0] + v[1] * n[1] + v[2] * n[2];
  if (inward < 0) for (let i = 0; i < 3; i++) v[i] -= n[i] * inward;
}

/** Feet-anchored capsule; sweeps the complete requested travel, then slides.
 * A roof can stop upward movement without becoming support above the caller.
 * The same response is used by client characters and authoritative agents.
 * `out` may alias `from` or `to`; velocity is updated in place. */
export function moveCliffCapsule(
  field: CanyonCliffField, from: readonly number[], to: readonly number[], up: readonly number[],
  height: number, radius: number, velocity: number[], out: number[], s: CliffMotionScratch,
): boolean {
  for (let i = 0; i < 3; i++) { s.p[i] = from[i]; s.delta[i] = to[i] - from[i]; }
  let touched = false;
  // Spawn/load recovery is bounded and uses a genuine 3D separating normal.
  for (let iteration = 0; iteration < 4; iteration++) {
    endpoints(s, up, height, radius);
    const contact = field.capsuleContact(s.a, s.b, radius, s.hit);
    if (!contact || contact.penetration <= 1e-7) break;
    touched = true;
    for (let i = 0; i < 3; i++) s.p[i] += contact.normal[i] * (contact.penetration + SKIN_M);
    rejectInto(velocity, contact.normal);
  }
  for (let iteration = 0; iteration < 4; iteration++) {
    const length = Math.sqrt(s.delta[0] * s.delta[0] + s.delta[1] * s.delta[1] + s.delta[2] * s.delta[2]);
    if (length < 1e-8) break;
    endpoints(s, up, height, radius);
    const hit = field.capsuleSweep(s.a, s.b, s.delta, radius, s.hit);
    if (!hit) { for (let i = 0; i < 3; i++) s.p[i] += s.delta[i]; break; }
    touched = true;
    const t = Math.max(0, hit.t - SKIN_M / length);
    for (let i = 0; i < 3; i++) {
      s.p[i] += s.delta[i] * t;
      s.delta[i] *= 1 - t;
      if (hit.penetration > 0) s.p[i] += hit.normal[i] * (hit.penetration + SKIN_M);
    }
    rejectInto(s.delta, hit.normal);
    rejectInto(velocity, hit.normal);
  }
  for (let i = 0; i < 3; i++) out[i] = s.p[i];
  return touched;
}
